import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image, Video, X, Loader2 } from 'lucide-react';
import { useCreatePost } from '../../hooks/use-posts';
import { useUploadPostMedia } from '../../hooks/use-file';
import type { CreatePostData } from '../../types/post';
import toast from 'react-hot-toast';

type FloatingCreatePostProps = {
  /**
   * Ouverture pilotée de l'extérieur — le bouton « + » de la barre du bas sur
   * mobile. Sans ces props, le composant garde son propre bouton flottant.
   */
  ouvert?: boolean;
  onFermer?: () => void;
};

const FloatingCreatePost: React.FC<FloatingCreatePostProps> = ({ ouvert, onFermer }) => {
  const pilote = ouvert !== undefined;
  const [ouvertInterne, setOuvertInterne] = useState(false);
  const isOpen = pilote ? ouvert : ouvertInterne;
  const setIsOpen = (valeur: boolean) => {
    if (pilote) {
      if (!valeur) onFermer?.();
    } else {
      setOuvertInterne(valeur);
    }
  };
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'friends' | 'private'>('public');
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const createPostMutation = useCreatePost();
  const uploadPostMedia = useUploadPostMedia();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const newMediaFiles = [...mediaFiles, ...files].slice(0, 4); // Max 4 files
    setMediaFiles(newMediaFiles);

    // Create previews
    const newPreviews: string[] = [];
    newMediaFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result as string);
        if (newPreviews.length === newMediaFiles.length) {
          setMediaPreviews(newPreviews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeMedia = (index: number) => {
    setMediaFiles(mediaFiles.filter((_, i) => i !== index));
    setMediaPreviews(mediaPreviews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim() && mediaFiles.length === 0) {
      return;
    }

    try {
      let mediaUrls: string[] = [];
      let mediaTypes: ('image' | 'video')[] = [];

      // Upload des médias si présents
      if (mediaFiles.length > 0) {
        setIsUploading(true);
        try {
          const uploadedFiles = await uploadPostMedia.mutateAsync({
            files: mediaFiles,
          });

          // Extraire les URLs et types des fichiers uploadés
          mediaUrls = uploadedFiles.map((file) => file.url);
          mediaTypes = uploadedFiles.map((file) => 
            file.file_type === 'image' ? 'image' as const : 'video' as const
          );
        } catch (uploadError) {
          console.error('Error uploading media:', uploadError);
          toast.error('Erreur lors de l\'upload des médias');
          setIsUploading(false);
          return;
        }
        setIsUploading(false);
      }

      // Créer le post avec les URLs des médias uploadés
      const postData: CreatePostData = {
        content: content.trim(),
        visibility,
        media_urls: mediaUrls.length > 0 ? mediaUrls : undefined,
        media_types: mediaTypes.length > 0 ? mediaTypes : undefined,
      };

      await createPostMutation.mutateAsync(postData);
      
      // Réinitialiser le formulaire
      setContent('');
      setVisibility('public');
      setMediaFiles([]);
      setMediaPreviews([]);
      setIsOpen(false);
      toast.success('Post publié avec succès !');
      
      // Rediriger vers le feed si pas déjà dessus
      navigate('/feed');
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Erreur lors de la publication du post');
    }
  };

  return (
    <>
      {/* Modal backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Créer un post</h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Quoi de neuf ? Partagez vos moments..."
                    className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    maxLength={5000}
                    autoFocus
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">{content.length} / 5000</span>
                  </div>
                </div>

                {/* Media preview */}
                {mediaPreviews.length > 0 && (
                  <div className="mb-4 grid grid-cols-2 gap-3">
                    {mediaPreviews.map((preview, index) => (
                      <div key={index} className="relative group aspect-video rounded-xl overflow-hidden bg-gray-100">
                        {mediaFiles[index]?.type.startsWith('video/') ? (
                          <video
                            src={preview}
                            className="w-full h-full object-cover"
                            controls
                          />
                        ) : (
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => removeMedia(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Media buttons */}
                <div className="mb-4 flex items-center gap-2 pb-4 border-b border-gray-200">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={mediaFiles.length >= 4}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Image className="w-5 h-5" />
                    <span className="text-sm font-medium">Photo</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={mediaFiles.length >= 4}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Video className="w-5 h-5" />
                    <span className="text-sm font-medium">Vidéo</span>
                  </button>
                  {mediaFiles.length > 0 && (
                    <span className="text-xs text-gray-500 ml-auto">
                      {mediaFiles.length}/4 fichiers
                    </span>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Visibilité</label>
                  <select
                    value={visibility}
                    onChange={(e) =>
                      setVisibility(e.target.value as 'public' | 'friends' | 'private')
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="public">🌍 Public</option>
                    <option value="friends">👥 Amis</option>
                    <option value="private">🔒 Privé</option>
                  </select>
                </div>

                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      setContent('');
                      setVisibility('public');
                      setMediaFiles([]);
                      setMediaPreviews([]);
                    }}
                    className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={(!content.trim() && mediaFiles.length === 0) || createPostMutation.isPending || isUploading}
                    className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                  >
                    {isUploading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Upload médias...
                      </span>
                    ) : createPostMutation.isPending ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Publication...
                      </span>
                    ) : (
                      'Publier'
                    )}
                  </button>
                </div>

                {createPostMutation.isError && (
                  <div className="mt-4 text-sm text-red-600">
                    Erreur lors de la publication. Veuillez réessayer.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Button : remplacé par le « + » de la barre du bas quand il est piloté. */}
      {!pilote && (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-br from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-full shadow-2xl hover:shadow-green-500/50 transition-all duration-300 flex items-center justify-center z-30 group hover:scale-110 active:scale-95"
        title="Créer un post"
      >
        <div className="absolute inset-0 rounded-full bg-white/20 blur-xl group-hover:bg-white/30 transition-all"></div>
        <svg
          className="w-7 h-7 relative z-10 transition-transform group-hover:rotate-90 duration-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        <div className="absolute inset-0 rounded-full ring-4 ring-green-500/20 group-hover:ring-green-500/40 transition-all"></div>
      </button>
      )}
    </>
  );
};

export default FloatingCreatePost;
